import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import './Horses.css';

const Horses = ({ user }) => {
  const [horses, setHorses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({
    name: '', breed: '', age: '', gender: '', color: '', height_hands: '', weight_lbs: '', notes: '',
  });
  const location = useLocation();

  useEffect(() => {
    if (location.pathname === '/horses/new') setShowForm(true);
  }, [location.pathname]);

  useEffect(() => {
    if (user) loadHorses();
  }, [user]);

  const loadHorses = async () => {
    const { data } = await supabase
      .from('horses')
      .select('*')
      .eq('owner_id', user.id)
      .order('created_at', { ascending: false });
    setHorses(data || []);
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    const horseData = {
      owner_id: user.id,
      name: form.name,
      breed: form.breed || null,
      age: form.age ? parseInt(form.age) : null,
      gender: form.gender || null,
      color: form.color || null,
      height_hands: form.height_hands ? parseFloat(form.height_hands) : null,
      weight_lbs: form.weight_lbs ? parseInt(form.weight_lbs) : null,
      notes: form.notes || null,
    };

    const { error: insertError } = await supabase.from('horses').insert(horseData);

    if (insertError) {
      setError(insertError.message);
    } else {
      setForm({ name: '', breed: '', age: '', gender: '', color: '', height_hands: '', weight_lbs: '', notes: '' });
      setShowForm(false);
      loadHorses();
    }
    setSaving(false);
  };

  const deleteHorse = async (id, name) => {
    if (!window.confirm(`Are you sure you want to remove ${name}?`)) return;
    await supabase.from('horses').delete().eq('id', id);
    loadHorses();
  };

  if (loading) {
    return (
      <div className="horses-container">
        <div className="loading-spinner">Loading horses...</div>
      </div>
    );
  }

  return (
    <div className="horses-container">
      <div className="horses-header">
        <h1>🐎 My Horses</h1>
        <button onClick={() => setShowForm(!showForm)} className="add-horse-button">
          {showForm ? '✕ Cancel' : '+ Add Horse'}
        </button>
      </div>

      {/* Add Horse Form */}
      {showForm && (
        <div className="horse-form-card">
          <h3>Add a New Horse</h3>
          {error && <div className="auth-error">{error}</div>}
          <form onSubmit={handleSubmit} className="horse-form">
            <div className="form-row">
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Thunder"
                  required
                />
              </div>
              <div className="form-group">
                <label>Breed</label>
                <input
                  type="text"
                  value={form.breed}
                  onChange={(e) => setForm({ ...form, breed: e.target.value })}
                  placeholder="Quarter Horse"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Age (years)</label>
                <input
                  type="number"
                  value={form.age}
                  onChange={(e) => setForm({ ...form, age: e.target.value })}
                  placeholder="8"
                  min="0"
                  max="50"
                />
              </div>
              <div className="form-group">
                <label>Gender</label>
                <select
                  value={form.gender}
                  onChange={(e) => setForm({ ...form, gender: e.target.value })}
                >
                  <option value="">Select...</option>
                  <option value="mare">Mare</option>
                  <option value="gelding">Gelding</option>
                  <option value="stallion">Stallion</option>
                  <option value="colt">Colt</option>
                  <option value="filly">Filly</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Color</label>
                <input
                  type="text"
                  value={form.color}
                  onChange={(e) => setForm({ ...form, color: e.target.value })}
                  placeholder="Bay"
                />
              </div>
              <div className="form-group">
                <label>Height (hands)</label>
                <input
                  type="number"
                  value={form.height_hands}
                  onChange={(e) => setForm({ ...form, height_hands: e.target.value })}
                  placeholder="15.2"
                  step="0.1"
                  min="8"
                  max="20"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Notes</label>
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Any additional notes about your horse..."
                rows="3"
              />
            </div>

            <button type="submit" className="auth-button" disabled={saving}>
              {saving ? 'Adding...' : 'Add Horse'}
            </button>
          </form>
        </div>
      )}

      {/* Horses List */}
      {horses.length === 0 && !showForm ? (
        <div className="empty-state">
          <span className="empty-icon">🐴</span>
          <h3>No horses yet</h3>
          <p>Add your first horse to start tracking their health and stride analysis</p>
          <button onClick={() => setShowForm(true)} className="auth-button">
            Add Your First Horse
          </button>
        </div>
      ) : (
        <div className="horses-list">
          {horses.map((horse) => (
            <div key={horse.id} className="horse-list-card">
              <div className="horse-list-avatar">🐴</div>
              <div className="horse-list-info">
                <h3>{horse.name}</h3>
                <p>
                  {[
                    horse.breed,
                    horse.age && `${horse.age} yrs`,
                    horse.gender,
                    horse.color,
                    horse.height_hands && `${horse.height_hands}hh`,
                  ]
                    .filter(Boolean)
                    .join(' • ')}
                </p>
                {horse.notes && <p className="horse-notes">{horse.notes}</p>}
              </div>
              <div className="horse-list-actions">
                <button
                  onClick={() => deleteHorse(horse.id, horse.name)}
                  className="delete-button"
                  title="Remove horse"
                >
                  🗑️
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Horses;
